import { useState, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { getPortalDOMId, getPortalKey } from './common';
/**
 * @component Portal
 * @description A React component that creates a portal using `ReactDOM.createPortal`.
 * It dynamically finds the target DOM element based on the `name` prop (which translates to an ID like `portal-name`).
 * This component is designed to be resilient to cases where the target DOM element (PortalHost)
 * might be mounted after the Portal component itself, or might be unmounted and remounted (e.g., due to navigation).
 *
 * How it works:
 * 1. State (`element`): Holds a reference to the target DOM element where children will be portaled.
 *    It's initialized by trying to find the element immediately.
 * 2. `isElementCurrentlyAttached`: A boolean calculated during each render, checking if the `element` in state
 *    is currently attached to `document.body`. This is crucial for the effect's dependency array.
 * 3. `useLayoutEffect`: This effect hook is responsible for finding and verifying the target DOM element.
 *    - It runs after every render if `id`, `waitHost`, `element` (reference), `name`, or significantly,
 *      `isElementCurrentlyAttached` changes.
 *    - If the `element` in state is valid and attached: It does nothing.
 *    - If the `element` is stale (detached) or null: It attempts to find the DOM element by its ID.
 *      - If found, it updates the `element` state, triggering a re-render and re-evaluation.
 *      - If not found and `element` was stale, it sets `element` state to `null` to ensure a clean state for the next attempt.
 *      - If not found and `waitHost` is true: It sets up an interval to periodically check for the element.
 *        Once found, the interval is cleared, and `element` state is updated.
 * 4. Rendering: If a valid, attached `element` is available, `createPortal` is used to render `children` into it.
 *    Otherwise, it renders `null`.
 *
 * This ensures that the portal correctly attaches to its host even if the host's availability in the DOM changes dynamically.
 */
export const Portal = ({ name, children, waitHost = false }) => {
    const id = getPortalDOMId(name);
    const [element, setElement] = useState(() => {
        if (typeof document !== 'undefined') {
            return document.getElementById(id);
        }
        return null;
    });
    const isElementCurrentlyAttached = typeof document !== 'undefined' && element ? document.body.contains(element) : false;
    useLayoutEffect(() => {
        if (typeof document === 'undefined') { // SSR Guard
            return;
        }
        if (element && isElementCurrentlyAttached && element.id === id) {
            // Element is valid, attached, and has the correct ID. No action needed.
            return;
        }
        // Element is stale, detached, null, or has the wrong ID.
        const currentDOMElement = document.getElementById(id);
        if (currentDOMElement) {
            // Found the correct DOM element.
            setElement(currentDOMElement);
            return;
        }
        // Correct DOM element not found.
        if (element !== null) {
            // If state held a stale/incorrect element, clear it to ensure a fresh attempt or interval setup.
            setElement(null);
            return;
        }
        // At this point, element in state is null, and currentDOMElement is also null.
        if (waitHost) {
            const interval = setInterval(() => {
                const e = document.getElementById(id);
                if (e) {
                    setElement(e);
                    clearInterval(interval);
                }
            }, 100);
            return () => {
                clearInterval(interval);
            };
        }
    }, [id, waitHost, element, name, isElementCurrentlyAttached]);
    if (element && isElementCurrentlyAttached) {
        return createPortal(children, element, getPortalKey(name));
    }
    return null;
};
